import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RangeState, ShotSession, PersonalBest, RangeStats } from './types';
import { ACHIEVEMENTS } from './types';

/**
 * Range log + stats hook. Owns the persisted shot-session log and derives
 * stats / PBs / achievements from it.
 *
 * Persistence: localStorage key 'sparrow.range.v1'. When customer-auth +
 * subscription billing lands (FREEMIUM 5), the same shape syncs to the
 * customer metafield via the sync worker so the log carries across devices.
 */

const STORAGE_KEY = 'sparrow.range.v1';
const INITIAL: RangeState = { version: 1, sessions: [], personalBests: {} };

export interface UseRange {
  state: RangeState;
  stats: RangeStats;
  /** Log a new session — generates id + computes PB updates. */
  logSession: (s: Omit<ShotSession, 'id' | 'loggedAt'> & { loggedAt?: string }) => ShotSession;
  /** Delete a session (e.g. typo'd entry). Recomputes PBs after. */
  deleteSession: (id: string) => void;
  /** v136.0 — bulk replace the session list (used by cloud-sync merge). */
  replaceSessions: (sessions: ShotSession[]) => void;
  /** Wipe everything — for testing or user request. */
  reset: () => void;
}

export function useRange(): UseRange {
  const [state, setState] = useState<RangeState>(() => loadState());

  useEffect(() => {
    saveState(state);
  }, [state]);

  const logSession: UseRange['logSession'] = useCallback((s) => {
    const session: ShotSession = {
      ...s,
      id: genId(),
      loggedAt: s.loggedAt ?? new Date().toISOString(),
    };
    setState((prev) => {
      const sessions = [session, ...prev.sessions];
      const personalBests = recomputePBs(sessions);
      return { ...prev, sessions, personalBests };
    });
    return session;
  }, []);

  const deleteSession: UseRange['deleteSession'] = useCallback((id) => {
    setState((prev) => {
      const sessions = prev.sessions.filter((s) => s.id !== id);
      const personalBests = recomputePBs(sessions);
      return { ...prev, sessions, personalBests };
    });
  }, []);

  const reset = useCallback(() => setState(INITIAL), []);

  const replaceSessions: UseRange['replaceSessions'] = useCallback((sessions) => {
    setState((prev) => {
      const personalBests = recomputePBs(sessions);
      return { ...prev, sessions, personalBests };
    });
  }, []);

  const stats = useMemo(() => computeStats(state), [state]);

  return { state, stats, logSession, deleteSession, replaceSessions, reset };
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Sandbox-safe localStorage access.
 *
 * In a sandboxed iframe WITHOUT `allow-same-origin`, even reading
 * `window.localStorage` (or `typeof localStorage`) throws a SecurityError
 * BEFORE you can call .getItem(). The try/catch has to wrap the entire
 * access expression, not just the call.
 *
 * Same situation as the bulletproof storage shim added in task #126 for the
 * Shopify-embedded iframe. Reuse that pattern here.
 */
function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch { /* sandbox, quota, or private-mode — silently ignore */ }
}

function loadState(): RangeState {
  const raw = safeGetItem(STORAGE_KEY);
  if (!raw) return INITIAL;
  try {
    const parsed = JSON.parse(raw) as RangeState;
    if (parsed.version !== 1) return INITIAL;
    return parsed;
  } catch {
    return INITIAL;
  }
}

function saveState(state: RangeState): void {
  safeSetItem(STORAGE_KEY, JSON.stringify(state));
}

function genId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Recompute PBs from full session history. Called whenever sessions change.
 * Only counts sessions with ≥4 arrows (anything less isn't a meaningful group).
 */
function recomputePBs(sessions: readonly ShotSession[]): Record<string, PersonalBest> {
  const pbs: Record<string, PersonalBest> = {};
  for (const s of sessions) {
    if (s.arrowCount < 4) continue;
    const key = String(s.distanceYd);
    const existing = pbs[key];
    if (!existing || s.groupSizeIn < existing.groupSizeIn) {
      pbs[key] = {
        distanceYd: s.distanceYd,
        groupSizeIn: s.groupSizeIn,
        sessionId: s.id,
        achievedAt: s.loggedAt,
        arrowCount: s.arrowCount,
      };
    }
  }
  return pbs;
}

/**
 * Derive stats from session log. Streaks based on UTC-day buckets.
 */
function computeStats(state: RangeState): RangeStats {
  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;
  const sessions = state.sessions;

  const totalSessions = sessions.length;
  const totalArrows = sessions.reduce((sum, s) => sum + s.arrowCount, 0);
  const sessionsThisWeek = sessions.filter(
    (s) => now - new Date(s.loggedAt).getTime() <= 7 * oneDayMs,
  ).length;
  const sessionsThisMonth = sessions.filter(
    (s) => now - new Date(s.loggedAt).getTime() <= 30 * oneDayMs,
  ).length;

  const { current, longest } = computeStreaks(sessions);

  // Run all achievement tests against the current state
  const partialStats = {
    totalSessions, totalArrows, sessionsThisWeek, sessionsThisMonth,
    currentStreakDays: current, longestStreakDays: longest, achievements: [] as string[],
  };
  const achievements = ACHIEVEMENTS
    .filter((a) => a.test(state, partialStats))
    .map((a) => a.id);

  return { ...partialStats, achievements };
}

/**
 * Bucket sessions by UTC date string, then walk backward from today to find
 * the longest contiguous streak. Current streak = today or yesterday must be
 * in the log (a one-day grace so people who haven't logged today's session
 * yet don't lose their streak).
 */
function computeStreaks(sessions: readonly ShotSession[]): { current: number; longest: number } {
  if (sessions.length === 0) return { current: 0, longest: 0 };

  const dayKey = (iso: string) => iso.slice(0, 10); // 'YYYY-MM-DD'
  const days = new Set(sessions.map((s) => dayKey(s.loggedAt)));

  // Compute longest streak across all logged days
  const sortedDays = Array.from(days).sort();
  let longest = 0;
  let run = 0;
  let prev: string | null = null;
  for (const d of sortedDays) {
    if (prev === null) {
      run = 1;
    } else {
      const diff =
        (new Date(d).getTime() - new Date(prev).getTime()) / (24 * 60 * 60 * 1000);
      run = diff === 1 ? run + 1 : 1;
    }
    longest = Math.max(longest, run);
    prev = d;
  }

  // Compute current streak — walk backward from today (with 1-day grace)
  const today = new Date().toISOString().slice(0, 10);
  const yest = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  let cursor = days.has(today) ? today : days.has(yest) ? yest : null;
  let current = 0;
  while (cursor && days.has(cursor)) {
    current++;
    cursor = new Date(new Date(cursor).getTime() - 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  }
  return { current, longest };
}
